// Explorer (B6, owner's decision 2026-10-14 §5.3): the kit-free card set, for trips with no language
// kit. Its cards come from the trip itself, as the child may see it: the pack a card set gets is
// already masked by their controls (domain/trip/visible), and Explorer also skips wrapped stages,
// masked legs ("???") and surprise plans, so a hidden stage or surprise never leaks. Card ids
// name what they ask about, so SRS works as for the other card sets.
//   ex:day:<item>         "Which day do we go to …?"
//   ex:after:<stage>      "What comes after …?"
//   ex:leg:<leg>          "How do we get from … to …?"
//   ex:today              "Which of these is on today's plan?" (or the next day with plans)
//   ex:wx:<stage>         the stage's typical weather for its month
//   ex:far:<a>:<b>        "Which is further from …?"
//   ex:days               "How many days is the trip?" (always safe: works with one stage, no plans)
//   ex:fact:<id>          a fact the grown-up added (Organiser → Trips → Facts for Explorer)
import type { Climate, DayItem, LegKind, Pack, Stage } from '../../../../shared/pack/schema'
import { MYSTERY_SPOT } from '../../../../shared/tripVisible'
import { addDays, daysBetween } from '../../time'
import { choices } from '../cards'
import type { Rng } from '../../rng'
import { isLearned } from '../srs'
import type { CardSet, ChoiceItem } from '../types'
import { t } from '../../../strings/en'

const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December']
const MONTHS_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
const dayLabel = (d: string) => {
  const x = new Date(`${d}T00:00:00Z`)
  return `${DAYS[x.getUTCDay()]} ${x.getUTCDate()} ${MONTHS_SHORT[x.getUTCMonth()]}`
}
const LEG_WORD: Record<LegKind, string> = { flight: 'By plane', train: 'By train', bus: 'By bus', lift: 'By lift', car: 'By car', walk: 'On foot', ferry: 'By ferry' }
const WX_WORD: Record<Climate['condition'], string> = { snow: 'Snowy', clear: 'Sunny', cloudy: 'Cloudy', rain: 'Rainy' }
const XP = 10

/** Stages the child can see by name (a wrapped stage is spot `mystery`). */
const shownStages = (pack: Pack): Stage[] => pack.itinerary.stages.filter((s) => s.spot !== MYSTERY_SPOT)
/** Plans that are what they say: no surprise still showing its teaser. */
const plainItems = (pack: Pack): DayItem[] => pack.itinerary.dayItems.filter((i) => !i.surprise || i.title !== i.surprise.teaser)
const placeName = (pack: Pack, i: DayItem) => (i.placeId ? pack.places.find((p) => p.id === i.placeId)?.name : undefined) ?? i.title
const legsShown = (pack: Pack) => pack.itinerary.legs.filter((l) => l.from.name !== '???' && l.to.name !== '???')

function km(a: readonly [number, number], b: readonly [number, number]): number {
  const r = (d: number) => (d * Math.PI) / 180
  const h = Math.sin(r(b[0] - a[0]) / 2) ** 2 + Math.cos(r(a[0])) * Math.cos(r(b[0])) * Math.sin(r(b[1] - a[1]) / 2) ** 2
  return 6371 * 2 * Math.asin(Math.sqrt(h))
}

/** Pairs of planned places far enough apart to tell (at least 20 % and 3 km different), from the first stage. */
function farPairs(pack: Pack): [string, string][] {
  const home = shownStages(pack)[0]?.anchor
  if (!home) return []
  const seen = new Set<string>()
  const places = plainItems(pack).flatMap((i) => {
    const p = i.placeId ? pack.places.find((x) => x.id === i.placeId) : undefined
    if (!p || seen.has(p.id)) return []
    seen.add(p.id)
    return [{ id: p.id, d: km(home, p.at) }]
  })
  const out: [string, string][] = []
  for (let i = 0; i < places.length; i++)
    for (let j = i + 1; j < places.length && out.length < 12; j++) {
      const [a, b] = [places[i]!, places[j]!]
      if (Math.abs(a.d - b.d) >= Math.max(3, 0.2 * Math.max(a.d, b.d))) out.push([a.id, b.id])
    }
  return out
}

const climateOf = (pack: Pack, s: Stage): Climate | undefined => pack.meta.climatology[s.regionId]?.[s.startDate.slice(5, 7)]

function item(id: string, question: string, answer: string, near: string[], rand: Rng, context?: string): ChoiceItem | null {
  const c = choices(answer, near, [], rand)
  if (c.options.length < 2) return null
  return { type: 'choice', cardId: id, ...(context ? { context } : {}), text: question, question: '', options: c.options, answer: c.answer, reveal: { line: answer }, xp: XP }
}

export const explorer: CardSet = {
  id: 'explorer',
  kind: 'explore',
  title: 'Explorer',
  blurb: t.arcade.blurbs['explorer']!,
  theme: { glyph: '?!', jpGlyph: false, color: 'mint' },
  sessionSize: 5,
  newPerSession: 3,

  pool(pack: Pack) {
    const stages = shownStages(pack)
    const ids = ['ex:days', ...(plainItems(pack).length ? ['ex:today'] : [])]
    for (const i of plainItems(pack)) ids.push(`ex:day:${i.id}`)
    for (const s of stages) {
      const next = pack.itinerary.stages[pack.itinerary.stages.indexOf(s) + 1]
      if (next && next.spot !== MYSTERY_SPOT && next.name !== s.name) ids.push(`ex:after:${s.id}`)
      if (climateOf(pack, s)) ids.push(`ex:wx:${s.id}`)
    }
    for (const l of legsShown(pack)) ids.push(`ex:leg:${l.id}`)
    for (const [a, b] of farPairs(pack)) ids.push(`ex:far:${a}:${b}`)
    // B6: the grown-up's own facts, asked when there's something to choose between.
    const facts = pack.meta.facts ?? []
    for (const f of facts) if ((f.wrong?.length ?? 0) > 0 || facts.length > 1) ids.push(`ex:fact:${f.id}`)
    return ids
  },

  eligible: () => true,

  toItem(ctx, id, rand) {
    const pack = ctx.pack
    const { startDate, endDate, title } = pack.meta
    const allDays = Array.from({ length: daysBetween(startDate, endDate) + 1 }, (_, i) => addDays(startDate, i))
    const [, kind, a, b] = id.split(':')
    if (kind === 'days') {
      const n = allDays.length
      const near = [n - 2, n - 1, n + 1, n + 2, n + 7].filter((x) => x > 0).map((x) => `${x} days`)
      return item(id, `How many days is ${title}?`, `${n} days`, near, rand)
    }
    if (kind === 'today') {
      const items = plainItems(pack)
      // Today's plans, or the nearest day ahead (else behind) that has some.
      const dates = [...new Set(items.map((i) => i.date))].sort()
      const day = dates.includes(ctx.day) ? ctx.day : (dates.find((d) => d > ctx.day) ?? dates.at(-1))
      const that = items.filter((i) => i.date === day)
      if (!day || !that.length) return null
      const right = that[Math.floor(rand() * that.length)]!
      const others = items.filter((i) => i.date !== day && !that.some((t) => t.title === i.title)).map((i) => i.title)
      const q = day === ctx.day ? "Which of these is on today's plan?" : `Which of these is on the plan for ${dayLabel(day)}?`
      return item(id, q, right.title, others, rand, dayLabel(day))
    }
    if (kind === 'day') {
      const it = plainItems(pack).find((i) => i.id === a)
      if (!it) return null
      const near = allDays.filter((d) => d !== it.date && Math.abs(daysBetween(d, it.date)) <= 6).map(dayLabel)
      return item(id, `Which day do we go to ${placeName(pack, it)}?`, dayLabel(it.date), near.length >= 3 ? near : allDays.filter((d) => d !== it.date).map(dayLabel), rand)
    }
    if (kind === 'after') {
      const stages = pack.itinerary.stages
      const i = stages.findIndex((s) => s.id === a)
      const next = stages[i + 1]
      if (i < 0 || !next || next.spot === MYSTERY_SPOT) return null
      const names = [...new Set(shownStages(pack).map((s) => s.name))].filter((n) => n !== next.name && n !== stages[i]!.name)
      return item(id, `What comes after ${stages[i]!.name}?`, next.name, [...names, 'Home!'], rand)
    }
    if (kind === 'leg') {
      const l = legsShown(pack).find((x) => x.id === a)
      if (!l) return null
      return item(id, `How do we get from ${l.from.name} to ${l.to.name}?`, LEG_WORD[l.kind], Object.values(LEG_WORD), rand)
    }
    if (kind === 'wx') {
      const s = shownStages(pack).find((x) => x.id === a)
      const c = s && climateOf(pack, s)
      if (!s || !c) return null
      const line = (w: Climate['condition'], t: number) => `${WX_WORD[w]}, about ${Math.round(t)}°C`
      const right = line(c.condition, c.tempC)
      const near = (Object.keys(WX_WORD) as Climate['condition'][]).flatMap((w) => [line(w, c.tempC + (w === 'snow' ? -8 : 12)), line(w, c.tempC + 20)])
      return item(id, `What's the weather usually like in ${s.name} in ${MONTHS[Number(s.startDate.slice(5, 7)) - 1]}?`, right, near, rand)
    }
    if (kind === 'fact') {
      const facts = pack.meta.facts ?? []
      const f = facts.find((x) => x.id === a)
      if (!f) return null
      // Its own wrong answers first, then the other facts' answers.
      const near = [...(f.wrong ?? []), ...facts.filter((x) => x.id !== f.id).map((x) => x.a)].filter((x) => x !== f.a)
      return item(id, f.q, f.a, near, rand, t.explorer.factContext)
    }
    if (kind === 'far') {
      const home = shownStages(pack)[0]
      const pa = pack.places.find((p) => p.id === a)
      const pb = pack.places.find((p) => p.id === b)
      if (!home || !pa || !pb) return null
      const far = km(home.anchor, pa.at) > km(home.anchor, pb.at) ? pa : pb
      const c = choices(far.name, [pa.name, pb.name, 'About the same'], [], rand, 3)
      return { type: 'choice', cardId: id, text: `Which is further from ${home.name}?`, question: '', options: c.options, answer: c.answer, reveal: { line: far.name }, xp: XP }
    }
    return null
  },

  progress(ctx) {
    const pool = explorer.pool(ctx.pack)
    const learned = pool.filter((id) => isLearned(ctx.progress.cards[id])).length
    return pool.length ? { label: 'Explorer', value: learned, max: pool.length } : null
  },
}
